import { useContext, useState, useMemo, useCallback, memo } from 'react';
import { Box, Flex, Text } from '../ui';
import { isNotDefaultValue, addInputs } from '../../helpers/math/spadesMath';
import { useIndependentTeamScoring } from '../../helpers/utils/hooks';
import { GlobalContext } from '../../helpers/context/GlobalContext';
import { InputModal } from '../modals';
import DealerTag from '../ui/DealerTag';
import { ErrorModal } from '../modals';
import Unclaimed from './Unclaimed';
import { useValidateActuals, useGameScores } from '../../helpers/utils/hooks';
import {
  getActualsErrorText,
  isActualAutoGenerated,
} from '../../helpers/utils/helperFunctions';
import { useFeatureFlag } from '../../helpers/utils/useFeatureFlag';
import { FEATURE_FLAGS } from '../../helpers/utils/featureFlags';
import { getNames } from '../../helpers/utils/storage';

import type { Round, InputValue, ModalOpenArgs, Names } from '../../types';

interface TablePlayerInputProps {
  playerInput: InputValue;
  type: 'Bid' | 'Actual';
  playerName: string;
  isCurrent: boolean;
  roundHistory: Round[];
  index: number;
  dealerId: string;
  teamClassName: string;
  currentRound: Round;
  fieldToUpdate: string;
  onOpenModal: (args: ModalOpenArgs) => void;
  bidDisplay?: InputValue;
  showBid?: boolean;
  bidField?: string;
  position: 'left' | 'right' | 'top' | 'bottom';
  useTableRoundUI: boolean;
}

const TablePlayerInput = memo(
  ({
    playerInput,
    type,
    playerName,
    isCurrent,
    roundHistory,
    index,
    dealerId,
    teamClassName,
    currentRound,
    fieldToUpdate,
    onOpenModal,
    bidDisplay, // Pass bid specific string if needed
    showBid, // Pass boolean to show bid
    bidField, // Pass bid field for editing
    position, // New prop
    useTableRoundUI, // Passed from parent to avoid redundant hook calls
  }: TablePlayerInputProps) => {
    const displayValue =
      playerInput === ''
        ? ''
        : `${playerInput}${isActualAutoGenerated(fieldToUpdate, currentRound, type) ? '*' : ''}`;

    // Determine layout direction for Table UI
    const getLayoutStyles = () => {
      if (!useTableRoundUI) return { direction: 'column' };
      switch (position) {
        case 'left':
          return { direction: 'row', gap: 0.5 }; // Content -> Tag
        case 'right':
          return { direction: 'row-reverse', gap: 0.5 }; // Tag -> Content
        case 'top':
          return { direction: 'column', gap: 2 }; // Content -> Tag
        case 'bottom':
          return { direction: 'column-reverse', gap: 2 }; // Tag -> Content
        default:
          return { direction: 'column' };
      }
    };

    const layoutStyles = getLayoutStyles();

    // Reusable Dealer Tag Component
    const dealerTagComponent = (
      <Box zIndex={5} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <DealerTag
          id={dealerId}
          index={index}
          isCurrent={isCurrent}
          roundHistory={roundHistory}
        />
      </Box>
    );

    return (
      <Flex
        direction={layoutStyles.direction}
        align="center"
        justify="center"
        position="relative"
        gap={layoutStyles.gap}
      >
        <Flex
          direction="column"
          align="center"
          justify="center"
          cursor="pointer"
          onClick={() => onOpenModal({ fieldToUpdate, type, playerName })}
          p={2}
          borderRadius="md"
          _hover={{ bg: 'whiteAlpha.100' }}
          _focus={{ outline: 'none' }}
          title={`Edit ${type}`}
          data-cy={
            displayValue
              ? 'playerInput'
              : type === 'Bid'
                ? 'bidButton'
                : 'actualButton'
          }
          data-testid={
            displayValue
              ? 'playerInput'
              : type === 'Bid'
                ? 'bidButton'
                : 'actualButton'
          }
          position="relative"
        >
          <Flex align="center" mb={1} position="relative">
            <Text
              fontWeight="bold"
              fontSize="md"
              mr={!useTableRoundUI ? 1 : undefined}
            >
              {playerName}
            </Text>

            {/* Classic Mode Dealer Tag - Inline */}
            {!useTableRoundUI && type === 'Bid' && (
              <DealerTag
                id={dealerId}
                index={index}
                isCurrent={isCurrent}
                roundHistory={roundHistory}
              />
            )}
          </Flex>

          <Flex
            align="center"
            justify="center"
            border="1px solid"
            borderColor={teamClassName === 'team1' ? 'team1' : 'team2'}
            borderRadius="full"
            w="80px"
            h="40px"
            bg={
              displayValue
                ? teamClassName === 'team1'
                  ? 'team1'
                  : 'team2'
                : 'gray.900'
            }
          >
            <Text
              fontSize="lg"
              fontWeight="bold"
              color={
                displayValue
                  ? 'blue.900'
                  : teamClassName === 'team1'
                    ? 'team1'
                    : 'team2'
              }
              textAlign="center"
            >
              {displayValue || type}
            </Text>
          </Flex>

          {/* Bid Display Pill - Attached to the Input Box */}
          {showBid && (
            <Flex
              position={useTableRoundUI ? 'relative' : 'absolute'}
              top={useTableRoundUI ? 'auto' : '100%'}
              left={useTableRoundUI ? 'auto' : '50%'}
              transform={useTableRoundUI ? 'none' : 'translateX(-50%)'}
              mt={1}
              bg="gray.800"
              border="1px solid"
              borderColor="gray.600"
              borderRadius="full"
              px={2}
              py={0.5}
              align="center"
              justify="center"
              zIndex={20}
              minW="50px"
              cursor="pointer"
              _hover={{ bg: 'gray.700' }}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onOpenModal({
                  fieldToUpdate: bidField || '',
                  type: 'Bid',
                  playerName,
                });
              }}
            >
              <Text fontSize="xs" color="gray.300" fontWeight="bold">
                Bid {bidDisplay}
              </Text>
            </Flex>
          )}
        </Flex>

        {/* Table UI Dealer Tag - Flex Sibling */}
        {useTableRoundUI && dealerTagComponent}
      </Flex>
    );
  },
);
TablePlayerInput.displayName = 'TablePlayerInput';

interface TeamTotalDisplayProps {
  bidTotal: number | string;
  actualTotal: number | string;
  teamClassName: string;
  areActualsEntered: boolean;
  isActualsPhase?: boolean; // added this from line 472/504
}

const TeamTotalDisplay = memo(
  ({
    bidTotal,
    actualTotal,
    teamClassName,
    areActualsEntered,
  }: TeamTotalDisplayProps) => {
    return (
      <Flex
        direction="column"
        align="center"
        p={2}
        bg="blackAlpha.400"
        borderRadius="md"
        minW="84px"
      >
        <Text
          fontWeight="bold"
          fontSize="sm"
          color={teamClassName === 'team1' ? 'team1' : 'team2'}
          mb={1}
        >
          {areActualsEntered ? 'Bid:Made' : 'Bid'}
        </Text>
        <Text fontSize="2xl" fontWeight="bold" color="white">
          {areActualsEntered ? `${bidTotal} : ${actualTotal}` : bidTotal}
        </Text>
      </Flex>
    );
  },
);
TeamTotalDisplay.displayName = 'TeamTotalDisplay';

interface GameScoreDisplayProps {
  teamName: string;
  score: number;
  bags: number;
  teamClassName: string;
}

const GameScoreDisplay = memo(
  ({ teamName, score, bags, teamClassName }: GameScoreDisplayProps) => (
    <Flex direction="column" align="center" p={2} borderRadius="md">
      <Text
        fontWeight="bold"
        fontSize="md"
        color={teamClassName === 'team1' ? 'team1' : 'team2'}
        mb={1}
      >
        {teamName}
      </Text>
      <Text fontSize="4xl" fontWeight="bold" lineHeight="1">
        {score}
      </Text>
      <Text fontSize="xs" color="gray.400">
        {bags} bags
      </Text>
    </Flex>
  ),
);
GameScoreDisplay.displayName = 'GameScoreDisplay';

interface TableRoundProps {
  roundHistory: Round[];
  isCurrent?: boolean;
  roundIndex: number;
}

function TableRound({
  roundHistory,
  isCurrent = false,
  roundIndex,
}: TableRoundProps) {
  const names = getNames() || ({} as Names);
  const { team1Name, team2Name, t1p1Name, t1p2Name, t2p1Name, t2p2Name } =
    names;
  const { currentRound, resetCurrentRound, setRoundHistory } =
    useContext(GlobalContext);

  // Single hook call for feature flag - passed to child components as prop
  const [useTableRoundUI] = useFeatureFlag(FEATURE_FLAGS.TABLE_ROUND_UI);

  // Hook for independent scoring logic
  useIndependentTeamScoring(
    currentRound,
    resetCurrentRound,
    isNotDefaultValue,
    setRoundHistory,
    roundHistory,
  );

  const { team1Score, team2Score } = useGameScores();

  const { team1BidsAndActuals, team2BidsAndActuals } = currentRound || {
    team1BidsAndActuals: {},
    team2BidsAndActuals: {},
  };

  const allBidsEntered =
    isNotDefaultValue(team1BidsAndActuals.p1Bid) &&
    isNotDefaultValue(team1BidsAndActuals.p2Bid) &&
    isNotDefaultValue(team2BidsAndActuals.p1Bid) &&
    isNotDefaultValue(team2BidsAndActuals.p2Bid);

  // Calculate Unclaimed Bids
  const team1TotalBid = addInputs(
    team1BidsAndActuals.p1Bid,
    team1BidsAndActuals.p2Bid,
  );
  const team2TotalBid = addInputs(
    team2BidsAndActuals.p1Bid,
    team2BidsAndActuals.p2Bid,
  );
  const totalBids = team1TotalBid + team2TotalBid;
  const unclaimed = 13 - totalBids;

  // Nil checks for team card clickability
  const isTeam1Nil = [
    team1BidsAndActuals.p1Bid,
    team1BidsAndActuals.p2Bid,
  ].some((b) => b === 'Nil' || b === 'Blind Nil');
  const isTeam2Nil = [
    team2BidsAndActuals.p1Bid,
    team2BidsAndActuals.p2Bid,
  ].some((b) => b === 'Nil' || b === 'Blind Nil');

  // Validation Check for Actuals
  const [isValid, setIsValid] = useState(true);

  const p1Actual = team1BidsAndActuals.p1Actual || '';
  const p2Actual = team1BidsAndActuals.p2Actual || '';
  const p3Actual = team2BidsAndActuals.p1Actual || '';
  const p4Actual = team2BidsAndActuals.p2Actual || '';

  const allActualsSubmitted = (
    [p1Actual, p2Actual, p3Actual, p4Actual] as InputValue[]
  ).every((val) => isNotDefaultValue(val));
  const totalActuals = addInputs(p1Actual, p2Actual, p3Actual, p4Actual);

  useValidateActuals(allActualsSubmitted, totalActuals, setIsValid);

  // Modal State Logic Hoisted
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    fieldToUpdate: string;
    playerName: string;
    type: 'Bid' | 'Actual';
    onCustomUpdate?: (input: InputValue) => void;
  }>({
    isOpen: false,
    fieldToUpdate: '',
    playerName: '',
    type: 'Bid',
    onCustomUpdate: undefined,
  });

  const handleOpenModal = useCallback(
    ({ fieldToUpdate, type, playerName, onCustomUpdate }: ModalOpenArgs) => {
      setModalState({
        isOpen: true,
        fieldToUpdate,
        playerName,
        type,
        onCustomUpdate,
      });
    },
    [],
  );

  const handleCloseModal = useCallback(() => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  // Stable Team Modal Handlers
  const handleTeam1Modal = useCallback(
    () =>
      handleOpenModal({
        fieldToUpdate: 'team1Total',
        type: 'Actual',
        playerName: team1Name || 'Team 1',
        onCustomUpdate: undefined,
      }),
    [handleOpenModal, team1Name],
  );

  const handleTeam2Modal = useCallback(
    () =>
      handleOpenModal({
        fieldToUpdate: 'team2Total',
        type: 'Actual',
        playerName: team2Name || 'Team 2',
        onCustomUpdate: undefined,
      }),
    [handleOpenModal, team2Name],
  );

  // Base props map memoized at top level
  const propsMap = useMemo(
    () => ({
      bottom: {
        name: t1p1Name,
        team: 'team1',
        bid: team1BidsAndActuals.p1Bid,
        actual: team1BidsAndActuals.p1Actual,
        bidField: 'team1BidsAndActuals.p1Bid',
        actualField: 'team1BidsAndActuals.p1Actual',
        dealer: 'team1BidsAndActuals.p1Bid', // Dealer associated with bid usually
      },
      top: {
        name: t1p2Name,
        team: 'team1',
        bid: team1BidsAndActuals.p2Bid,
        actual: team1BidsAndActuals.p2Actual,
        bidField: 'team1BidsAndActuals.p2Bid',
        actualField: 'team1BidsAndActuals.p2Actual',
        dealer: 'team1BidsAndActuals.p2Bid',
      },
      left: {
        name: t2p1Name,
        team: 'team2',
        bid: team2BidsAndActuals.p1Bid,
        actual: team2BidsAndActuals.p1Actual,
        bidField: 'team2BidsAndActuals.p1Bid',
        actualField: 'team2BidsAndActuals.p1Actual',
        dealer: 'team2BidsAndActuals.p1Bid',
      },
      right: {
        name: t2p2Name,
        team: 'team2',
        bid: team2BidsAndActuals.p2Bid,
        actual: team2BidsAndActuals.p2Actual,
        bidField: 'team2BidsAndActuals.p2Bid',
        actualField: 'team2BidsAndActuals.p2Actual',
        dealer: 'team2BidsAndActuals.p2Bid',
      },
    }),
    [
      t1p1Name,
      t1p2Name,
      t2p1Name,
      t2p2Name,
      team1BidsAndActuals,
      team2BidsAndActuals,
    ],
  );

  const renderPlayer = (position: 'left' | 'right' | 'top' | 'bottom') => {
    const data = propsMap[position];

    // Determine Logic
    const isBidPhase = !allBidsEntered;
    const type = isBidPhase ? 'Bid' : 'Actual';
    const playerInput = isBidPhase ? data.bid : data.actual;
    const fieldToUpdate = isBidPhase ? data.bidField : data.actualField;

    return (
      <TablePlayerInput
        playerName={data.name}
        teamClassName={data.team}
        playerInput={playerInput}
        type={type}
        fieldToUpdate={fieldToUpdate}
        dealerId={data.dealer}
        isCurrent={isCurrent}
        roundHistory={roundHistory}
        currentRound={currentRound}
        index={roundIndex}
        onOpenModal={handleOpenModal}
        showBid={allBidsEntered}
        bidDisplay={data.bid}
        bidField={data.bidField}
        position={position}
        useTableRoundUI={useTableRoundUI}
      />
    );
  };

  return (
    <Box position="relative" w="100%" h="400px" my={4} data-cy="table-round">
      <ErrorModal
        isOpen={!isValid && !modalState.isOpen}
        setIsModalOpen={setIsValid}
        index={roundIndex}
        names={names}
        isCurrent={isCurrent}
        roundHistory={roundHistory}
        currentRound={currentRound}
        errorMessage={getActualsErrorText(totalActuals)}
        onOpenModal={handleOpenModal}
      />

      {/* Hoisted Input Modal */}
      <InputModal
        isCurrent={isCurrent}
        playerName={modalState.playerName}
        isOpen={modalState.isOpen}
        setIsModalOpen={handleCloseModal}
        type={modalState.type}
        fieldToUpdate={modalState.fieldToUpdate}
        currentRound={currentRound}
        roundHistory={roundHistory}
        index={roundIndex}
        onCustomUpdate={modalState.onCustomUpdate}
      />

      {/* Table Image / Background */}
      <Box
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        w="70%"
        h="60%"
        border="4px solid"
        borderColor="whiteAlpha.300"
        borderRadius="full"
        zIndex={0}
      />

      {/* Team 1 Game Score - Top Left */}
      <Box position="absolute" top="0" left="0" zIndex={10}>
        <GameScoreDisplay
          teamName={team1Name || 'Team 1'}
          score={team1Score.teamScore}
          bags={team1Score.teamBags}
          teamClassName="team1"
        />
      </Box>

      {/* Team 2 Game Score - Top Right */}
      <Box position="absolute" top="0" right="0" zIndex={10}>
        <GameScoreDisplay
          teamName={team2Name || 'Team 2'}
          score={team2Score.teamScore}
          bags={team2Score.teamBags}
          teamClassName="team2"
        />
      </Box>

      {/* Team 1 Round Totals - Bottom Left */}
      <Box position="absolute" bottom="0" left="0" zIndex={10}>
        <Flex direction="column" gap={1} align="stretch" minW="84px">
          {allBidsEntered && !isTeam1Nil && (
            <Box
              as="button"
              data-cy="team1TotalMade"
              onClick={handleTeam1Modal}
              bg="gray.900"
              _hover={{ bg: 'gray.800' }}
              borderRadius="md"
              py={1}
              px={2}
              fontSize="xs"
              fontWeight="bold"
              textAlign="center"
              border="1px solid"
              borderColor="team1"
              color="team1"
            >
              Total Made
            </Box>
          )}
          <TeamTotalDisplay
            bidTotal={team1TotalBid}
            actualTotal={addInputs(
              team1BidsAndActuals.p1Actual,
              team1BidsAndActuals.p2Actual,
            )}
            isActualsPhase={allBidsEntered}
            teamClassName="team1"
            areActualsEntered={
              isNotDefaultValue(team1BidsAndActuals.p1Actual) &&
              isNotDefaultValue(team1BidsAndActuals.p2Actual)
            }
          />
        </Flex>
      </Box>

      {/* Team 2 Round Totals - Bottom Right */}
      <Box position="absolute" bottom="0" right="0" zIndex={10}>
        <Flex direction="column" gap={1} align="stretch" minW="84px">
          {allBidsEntered && !isTeam2Nil && (
            <Box
              as="button"
              data-cy="team2TotalMade"
              onClick={handleTeam2Modal}
              bg="gray.900"
              _hover={{ bg: 'gray.800' }}
              borderRadius="md"
              py={1}
              px={2}
              fontSize="xs"
              fontWeight="bold"
              textAlign="center"
              border="1px solid"
              borderColor="team2"
              color="team2"
            >
              Total Made
            </Box>
          )}
          <TeamTotalDisplay
            bidTotal={team2TotalBid}
            actualTotal={addInputs(
              team2BidsAndActuals.p1Actual,
              team2BidsAndActuals.p2Actual,
            )}
            isActualsPhase={allBidsEntered}
            teamClassName="team2"
            areActualsEntered={
              isNotDefaultValue(team2BidsAndActuals.p1Actual) &&
              isNotDefaultValue(team2BidsAndActuals.p2Actual)
            }
          />
        </Flex>
      </Box>

      {/* Bottom Player (Me) - Order 1 */}
      <Box
        position="absolute"
        bottom="0"
        left="50%"
        transform="translateX(-50%)"
        zIndex={10}
      >
        {renderPlayer('bottom')}
      </Box>

      {/* Left Player (Opponent 1) - Order 2 */}
      <Box
        position="absolute"
        top="50%"
        left="0"
        transform="translateY(-50%)"
        zIndex={10}
      >
        {renderPlayer('left')}
      </Box>

      {/* Top Player (Partner) - Order 3 */}
      <Box
        position="absolute"
        top="0"
        left="50%"
        transform="translateX(-50%)"
        zIndex={10}
      >
        {renderPlayer('top')}
      </Box>

      {/* Right Player (Opponent 2) - Order 4 */}
      <Box
        position="absolute"
        top="50%"
        right="0"
        transform="translateY(-50%)"
        zIndex={10}
      >
        {renderPlayer('right')}
      </Box>

      {/* Center Info */}
      <Flex
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        direction="column"
        align="center"
        justify="center"
        zIndex={10}
      >
        <Text fontSize="xl" fontWeight="bold">
          Round {roundIndex + 1}
        </Text>
        <Unclaimed numUnclaimed={unclaimed} />
        {currentRound?.autoGeneratedActuals &&
          Object.values(currentRound.autoGeneratedActuals).some(
            (val) => val === true,
          ) && (
            <Text fontSize="xs" color="gray.500" mt={1}>
              * Auto-generated
            </Text>
          )}
        {allBidsEntered && (
          <Text fontSize="xs" color="gray.400" mt={1}>
            Enter Actuals
          </Text>
        )}
      </Flex>
    </Box>
  );
}

export default TableRound;
